import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faEnvelope,
  faLocationDot,
  faPhone,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebookF,
  faGithub,
  faInstagram,
  faLinkedinIn,
  faXTwitter,
} from "@fortawesome/free-brands-svg-icons";
import servicesData from "../../JsonFiles/Services.json";
import projectData from "../../JsonFiles/Projects.json";
import industriesData from "../../JsonFiles/Industries.json";
import "./Footer.css";

const MAP_URL =
  "https://www.google.com/maps/search/?api=1&query=Jai+Tulja+Bhavani+Complex+Telco+Road+MIDC+Bhosari+Pune+411026";

// Replace any URL below if your official social profile uses a different handle.
const SOCIAL_LINKS = [
  {
    label: "LinkedIn",
    href: "https://www.linkedin.com/company/axiomos-technologies/",
    icon: faLinkedinIn,
  },
  {
    label: "Instagram",
    href: "https://www.instagram.com/axiomostechnologies/",
    icon: faInstagram,
  },
  {
    label: "Facebook",
    href: "https://www.facebook.com/axiomostechnologies/",
    icon: faFacebookF,
  },
  {
    label: "GitHub",
    href: "https://github.com/axiomos",
    icon: faGithub,
  },
  {
    label: "X",
    href: "https://x.com/axiomos",
    icon: faXTwitter,
  },
];

const COMPANY_LINKS = [
  { label: "About us", to: "/about" },
  { label: "Careers", to: "/career" },
  { label: "Insights", to: "/blogs" },
  { label: "Contact", to: "/contact" },
];

const FooterLink = ({ to, children, className = "", ...props }) => {
  const handleClick = () => {
    window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
  };

  return (
    <RouterLink
      to={to}
      className={className}
      onClick={handleClick}
      {...props}
    >
      {children}
    </RouterLink>
  );
};

const FooterList = ({ title, titleTo, items, viewAllTo, viewAllLabel }) => (
  <nav className="footer__nav" aria-label={`${title} links`}>
    <h3 className="footer__heading">
      <FooterLink to={titleTo}>{title}</FooterLink>
    </h3>

    <ul className="footer__link-list">
      {items.map((item) => (
        <li key={item.id ?? item.to ?? item.label}>
          <FooterLink to={item.to ?? `${titleTo}/${item.id}`}>
            {item.title ?? item.label}
          </FooterLink>
        </li>
      ))}
    </ul>

    {viewAllTo && (
      <FooterLink className="footer__view-all" to={viewAllTo}>
        {viewAllLabel}
        <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
      </FooterLink>
    )}
  </nav>
);

const Footer = () => {
  const featuredProjects = projectData.slice(0, 4);
  const featuredServices = servicesData.slice(0, 6);
  const featuredIndustries = industriesData.slice(0, 6);

  return (
    <footer className="footer">
      <div className="footer__accent" aria-hidden="true" />

      <div className="footer__shell">
        <section className="footer__cta" aria-labelledby="footer-cta-title">
          <div className="footer__cta-copy">
            <span className="footer__eyebrow">
              Let&apos;s build what&apos;s next
            </span>
            <h2 id="footer-cta-title">
              Have an idea? We&apos;ll help you turn it into a reliable product.
            </h2>
          </div>

          <FooterLink className="footer__cta-button" to="/contact">
            Start a conversation
            <FontAwesomeIcon icon={faArrowRight} aria-hidden="true" />
          </FooterLink>
        </section>

        <div className="footer__divider" />

        <div className="footer__main">
          <section className="footer__brand" aria-label="Axiomos Technologies">
            <FooterLink
              className="footer__logo"
              to="/"
              aria-label="Axiomos home"
            >
              <img
                className="footer__logo-image"
                src="https://axiomos.in/logo.png"
                alt="Axiomos Technologies LLP"
              />
              <span className="footer__logo-text">
                <strong>AXIOMOS</strong>
                <small>TECHNOLOGIES LLP</small>
              </span>
            </FooterLink>

            <p className="footer__description">
              We design and engineer secure, scalable digital products that help
              ambitious businesses operate smarter and grow faster.
            </p>

            <address className="footer__contact">
              <a href="mailto:contact@axiomos.in">
                <span className="footer__contact-icon">
                  <FontAwesomeIcon icon={faEnvelope} aria-hidden="true" />
                </span>
                <span>
                  <small>Email us</small>
                  contact@axiomos.in
                </span>
              </a>

              <a href="tel:+917738899255">
                <span className="footer__contact-icon">
                  <FontAwesomeIcon icon={faPhone} aria-hidden="true" />
                </span>
                <span>
                  <small>Call us</small>
                  +91 77388 99255
                </span>
              </a>

              <a href={MAP_URL} target="_blank" rel="noopener noreferrer">
                <span className="footer__contact-icon">
                  <FontAwesomeIcon icon={faLocationDot} aria-hidden="true" />
                </span>
                <span>
                  <small>Visit us</small>
                  Office 43, Jai Tulja Bhavani Complex, MIDC Bhosari, Pune
                  411026
                </span>
              </a>
            </address>
          </section>

          <div className="footer__navigation-grid">
            <FooterList
              title="Services"
              titleTo="/services"
              items={featuredServices}
              viewAllTo="/services"
              viewAllLabel="All services"
            />

            <FooterList
              title="Industries"
              titleTo="/industries"
              items={featuredIndustries}
              viewAllTo="/industries"
              viewAllLabel="All industries"
            />

            <FooterList
              title="Portfolio"
              titleTo="/projects"
              items={featuredProjects}
              viewAllTo="/projects"
              viewAllLabel="View our work"
            />

            <FooterList
              title="Company"
              titleTo="/about"
              items={COMPANY_LINKS}
            />
          </div>
        </div>

        <div className="footer__bottom">
          <p>
            &copy; {new Date().getFullYear()} Axiomos Technologies LLP. All
            rights reserved.
          </p>

          <div className="footer__socials" aria-label="Axiomos social profiles">
            {SOCIAL_LINKS.map(({ label, href, icon }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Follow Axiomos on ${label}`}
                title={label}
              >
                <FontAwesomeIcon icon={icon} aria-hidden="true" />
              </a>
            ))}
          </div>

          <div className="footer__legal">
            <FooterLink to="/privacy-policy">Privacy</FooterLink>
            <FooterLink to="/terms">Terms</FooterLink>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
